import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { LineMask } from "../components/marketing/Reveal";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center px-6 noise-overlay">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="forgot-logo">
          <span className="h-8 w-8 rounded-lg bg-neon flex items-center justify-center font-display font-black text-ink text-sm">OX</span>
          <span className="font-display font-bold text-lg tracking-tight">OrthoPlani<span className="text-neon">X</span></span>
        </Link>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.0]">
          <LineMask delay={0.05}>Forgot your</LineMask>
          <LineMask delay={0.2} className="text-neon">password?</LineMask>
        </h1>

        {sent ? (
          <div className="mt-10 bg-ink-2/80 border border-neon/30 rounded-2xl p-6" data-testid="forgot-success">
            <MailCheck className="h-8 w-8 text-neon mb-4" />
            <p className="font-display font-bold text-lg">Check your inbox</p>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              If an account exists for <span className="text-white font-semibold">{email}</span>, we've sent a reset link. It's valid for 1 hour.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-5 text-slate-400 text-sm">Enter your account email and we'll send you a secure link to choose a new password.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="f-email" className="text-slate-300">Email</Label>
                <Input id="f-email" data-testid="forgot-email-input" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="you@clinic.com" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full rounded-full bg-neon text-ink font-bold hover:bg-neon/90 h-12 text-sm">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        )}

        <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-neon" data-testid="forgot-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
